import { ScreenerFilterGroupKeys } from '@toggle/toggle';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath } from 'react-router-dom';
import { WretchError } from 'wretch/resolver';

import { ToggleFilterProps } from '~/components/update-filter-options/updateFilterOptions';
import { useFilterActions } from '~/hooks/use-filter-actions/useFilterActions';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { appPaths } from '~/routes/app-paths';
import { create } from '~/stores/create-store/createStore';

import { useScreenerStore } from '../use-screener-store/useScreenerStore';

export enum ActionModal {
  SaveAs = 'save',
  Rename = 'rename',
  Delete = 'delete',
  UnsavedChanges = 'unsaved',
  UnsavedDraftChanges = 'unsavedDraft',
}

type ScreenerActions = {
  activeModal?: ActionModal;
  nextActions: Function[];
  setActiveModal: (props: {
    activeModal?: ActionModal;
    nextActions?: Function[];
  }) => void;
};

export const useScreenerActionsStore = create<ScreenerActions>(set => ({
  activeModal: undefined,
  queuedActions: [],
  nextActions: [],
  setActiveModal: ({ activeModal, nextActions = [] }) =>
    set({ activeModal, nextActions }),
}));

export const useScreenerActions = () => {
  const navigateTerminal = useTerminalNavigate();
  const { t } = useTranslation(['screener']);
  const [error, setError] = useState<{
    message: string;
    type: 'unknown' | 'validation';
  }>();
  const { setActiveModal, activeModal, nextActions } =
    useScreenerActionsStore();
  const {
    deleteSavedFilter,
    saveNewFilter,
    updateSavedFilter,
    unsavedChanges,
    unsavedDraftChanges,
    setActiveSavedScreen,
    activeSavedScreen,
    createDraftScreen,
  } = useScreenerStore();

  const isScreenerPage = !!matchPath(
    { path: appPaths.screener, end: false, caseSensitive: false },
    location.pathname
  );

  const { addFilterOption } = useFilterActions({ store: useScreenerStore });

  const openScreen = (id: string | null, name: string | null) => {
    setActiveSavedScreen(id);
    const isDraft = !id || !name;
    const pathname = isDraft ? appPaths.screener : `${appPaths.screener}/${id}`;
    navigateTerminal({
      to: pathname,
      crumb: {
        isAccessibleFromSideBar: true,
        label: isDraft
          ? `${t('common:sideNavigation.screener')}: ${t('screener:newScreen')}`
          : `${t('common:sideNavigation.screener')}: ${name}`,
      },
    });
  };

  const navigateToScreener = (screenId: string | null, name: string | null) => {
    if (!unsavedChanges() || !activeSavedScreen) {
      openScreen(screenId, name);
      return;
    }

    if (!isScreenerPage) {
      openScreen(activeSavedScreen?.id, name);
    }
    setActiveModal({
      activeModal: ActionModal.UnsavedChanges,
      nextActions: [() => openScreen(screenId, name)],
    });
  };

  const createNewDraft = (props?: {
    option?: ToggleFilterProps;
    group?: ScreenerFilterGroupKeys;
    skipUnsavedCheck?: boolean;
  }) => {
    const {
      option,
      group = 'Knowledge Graph Filters',
      skipUnsavedCheck = false,
    } = props ?? {};

    const actions = () => {
      navigateTerminal({
        to: appPaths.screener,
        crumb: {
          isAccessibleFromSideBar: true,
          label: `${t('common:sideNavigation.screener')}: ${t(
            'screener:createNew'
          )}`,
        },
      });
      createDraftScreen();
      if (option) {
        addFilterOption(option, group);
      }
    };

    if (skipUnsavedCheck) {
      return actions();
    }

    const hasScreenUnsaved = activeSavedScreen && unsavedChanges();
    const hasDraftUnsaved = unsavedDraftChanges();

    const draftAction = () => {
      setActiveSavedScreen(null);
      setActiveModal({
        activeModal: ActionModal.UnsavedDraftChanges,
        nextActions: [actions],
      });
    };

    if (!hasScreenUnsaved && !hasDraftUnsaved) {
      return actions();
    }

    if (!isScreenerPage) {
      navigateTerminal({
        to: appPaths.screener,
        crumb: {
          isAccessibleFromSideBar: true,
          label: `${t('common:sideNavigation.screener')}: ${t(
            'screener:createNew'
          )}`,
        },
      });
    }
    if (!hasScreenUnsaved && hasDraftUnsaved) {
      draftAction();
    } else {
      //display UnsavedDraftChanges modal after UnsavedChanges if there are both unsaved screen and draft changes
      setActiveModal({
        activeModal: hasScreenUnsaved
          ? ActionModal.UnsavedChanges
          : ActionModal.UnsavedDraftChanges,
        nextActions: hasDraftUnsaved ? [draftAction] : [actions],
      });
    }
  };

  const handleDontSave = () => {
    setActiveModal({ activeModal: undefined, nextActions: [] });
    nextActions.forEach(action => action());
  };

  const clearError = () => {
    setError(undefined);
  };

  const handleAPIError = (e: WretchError) => {
    if (e.status === 409) {
      setError({
        type: 'validation',
        message: t('screener:error.existingScreener'),
      });
    } else {
      setError({
        type: 'unknown',
        message: t('screener:error.somethingWentWrongTryAgain'),
      });
    }
  };

  const handleSaving = async (screenerName: string) => {
    const res = await saveNewFilter(screenerName);
    if (res.error) {
      handleAPIError(res.error);
    } else {
      setActiveModal({ activeModal: undefined, nextActions: [] });
      if (nextActions.length) {
        nextActions.forEach(action => action());
      } else {
        openScreen(res.data.id, screenerName);
      }
    }
    return res;
  };

  const handleRename = async (screenerName: string) => {
    if (activeSavedScreen) {
      const res = await updateSavedFilter(activeSavedScreen.id, screenerName);
      if (res.error) {
        handleAPIError(res.error);
      } else {
        setActiveModal({ activeModal: undefined });
        return res;
      }
    }
    return { error: true };
  };

  const handleDelete = async () => {
    if (activeSavedScreen?.id) {
      const res = await deleteSavedFilter(activeSavedScreen.id);
      if (res.error) {
        handleAPIError(res.error);
      } else {
        setActiveModal({ activeModal: undefined });
        createDraftScreen({ forceNew: false });
      }
    }
  };

  const handleUnsavedChanges = async () => {
    if (activeSavedScreen) {
      const res = await updateSavedFilter(activeSavedScreen.id);
      if (res.error) {
        handleAPIError(res.error);
      } else {
        setActiveModal({ activeModal: undefined, nextActions: [] });
        nextActions.forEach(action => action());
      }
    } else {
      setActiveModal({
        activeModal: ActionModal.SaveAs,
        nextActions: !!nextActions.length
          ? nextActions
          : [() => createNewDraft({ skipUnsavedCheck: true })],
      });
    }
  };

  return {
    activeModal,
    nextActions,
    error,
    navigateToScreener,
    setActiveModal,
    createNewDraft,
    handleDontSave,
    handleUnsavedChanges,
    handleDelete,
    handleRename,
    setError,
    clearError,
    handleSaving,
  };
};
